import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Heart, Lock, Mic, ChevronRight, FileText, ExternalLink, Clock, Film, Users, Download, Image, FileImage, Newspaper, Share2, Printer, Calendar, Radio } from 'lucide-react';
import { getContentById } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';
import SEOHead, { generateEventSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChapterItem = ({ chapter, image }) => (
  <div className="group flex items-center gap-4 p-4 bg-[#141414] rounded-lg hover:bg-[#1f1f1f] cursor-pointer">
    <div className="relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-[#2a2a2a]">
      <img src={image} alt={chapter.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100" />
      {chapter.locked && <Lock className="absolute top-1 left-1 w-3 h-3 text-white/60" />}
      <div className="absolute inset-0 flex items-center justify-center">
        {chapter.type === 'Video' ? <Play className="w-6 h-6 text-white/80" /> : <Mic className="w-5 h-5 text-white/80" />}
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="text-white text-sm font-medium truncate group-hover:text-[#e4002b]">{chapter.title}</h4>
      <p className="text-white/50 text-xs truncate">{chapter.subtitle}</p>
      <span className="text-white/40 text-[10px] uppercase">{chapter.type}{chapter.duration && ` · ${chapter.duration}`}</span>
    </div>
    <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60" />
  </div>
);

const CastItem = ({ member }) => (
  <div className="flex justify-between items-center py-3 border-b border-white/10 last:border-0">
    <span className="text-white/80 text-sm font-medium uppercase">{member.performer}</span>
    <span className="text-white/50 text-sm">{member.character}</span>
  </div>
);

const CreditItem = ({ credit }) => (
  <div className="mb-4">
    <p className="text-white font-medium text-sm uppercase">{credit.name}</p>
    <p className="text-white/50 text-sm">{credit.role}</p>
  </div>
);

// Material download card component
const MaterialCard = ({ icon, title, description, url, isExternal = false }) => {
  const hasUrl = url && url !== '#';
  
  if (!hasUrl) {
    return (
      <div className="p-4 bg-[#141414] rounded-lg opacity-50">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/10 rounded-lg text-white/50">
            {icon}
          </div>
          <div className="flex-1">
            <h4 className="text-white/50 font-medium text-sm">{title}</h4>
            <p className="text-white/30 text-xs mt-1">No disponible</p>
          </div>
          <Lock className="w-4 h-4 text-white/30" />
        </div>
      </div>
    );
  }
  
  return (
    <a 
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={!isExternal}
      className="block p-4 bg-[#141414] hover:bg-[#1f1f1f] rounded-lg transition-colors group"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 bg-[#e4002b]/20 rounded-lg text-[#e4002b] group-hover:bg-[#e4002b]/30">
          {icon}
        </div>
        <div className="flex-1">
          <h4 className="text-white font-medium text-sm group-hover:text-[#e4002b] transition-colors">{title}</h4>
          <p className="text-white/50 text-xs mt-1">{description}</p>
        </div>
        {isExternal ? (
          <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-[#e4002b]" />
        ) : (
          <Download className="w-4 h-4 text-white/30 group-hover:text-[#e4002b]" />
        )}
      </div>
    </a>
  );
};

const ContentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('INFO');
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [cinemaSession, setCinemaSession] = useState(null);

  // Check if user is logged in as an approved cinema
  useEffect(() => {
    const session = localStorage.getItem('cinema_session');
    if (session) {
      const parsed = JSON.parse(session);
      setCinemaSession(parsed);
    }
  }, []);

  useEffect(() => { 
    const loadContent = async () => {
      setLoading(true);
      
      // First try to fetch from backend
      try {
        const res = await fetch(`${API_URL}/api/content/${id}`);
        if (res.ok) {
          const data = await res.json();
          // Map snake_case from backend to camelCase for frontend
          const mappedData = {
            ...data,
            heroImage: data.hero_image || data.image,
            synopsisExtended: data.synopsis_extended,
            artisticCredits: data.artistic_credits || [],
            productInfo: data.product_info || null,
            trailerUrl: data.trailer_url,
            premiereDate: data.premiere_date,
            isFeatured: data.is_featured,
            isRestricted: data.is_restricted,
            // Live streaming fields
            isLive: data.is_live || false,
            liveDate: data.live_date || '',
            liveTime: data.live_time || '',
            // Encore fields
            isEncore: data.is_encore || false,
            encoreStartDate: data.encore_start_date || '',
            encoreEndDate: data.encore_end_date || '',
            // Keep original fields too
            image: data.image,
            theater: data.theater,
            cast: data.cast || [],
            chapters: data.chapters || [],
            tags: data.tags || []
          };
          setContent(mappedData);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.log('Backend fetch failed, trying mock data');
      }
      
      // Fallback to mock data
      const mockContent = getContentById(id);
      setContent(mockContent);
      setLoading(false);
    };
    
    loadContent();
    
    // Load favorites from localStorage
    const savedFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setFavorites(savedFavorites);
  }, [id]);

  // Fetch existing media for this content (read-only for public)
  useEffect(() => {
    if (id) {
      fetch(`${API_URL}/api/media/content/${id}`)
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          const formatted = data.map(item => ({
            id: item.id,
            type: item.type,
            url: `${API_URL}${item.url}`,
            name: item.original_name,
            size: item.size
          }));
          setMediaItems(formatted);
        })
        .catch(() => setMediaItems([]));
    }
  }, [id]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><p className="text-white">Cargando...</p></div>;
  
  if (!content) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><p className="text-white">Contenido no encontrado</p></div>;

  // Check if cinema is approved
  const isCinemaApproved = cinemaSession?.status === 'approved' || cinemaSession?.cinema_id;
  
  const tabs = ['INFO', 'MULTIMEDIA', 'MATERIALES'];
  
  const isFavorite = favorites.includes(content.id);
  
  const toggleFavorite = () => {
    let newFavorites;
    if (isFavorite) {
      newFavorites = favorites.filter(f => f !== content.id);
    } else {
      newFavorites = [...favorites, content.id];
    }
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };

  const handleTrailer = () => {
    if (content.trailerUrl || content.trailer_url) {
      setShowTrailerModal(true);
    } else {
      alert('Trailer no disponible');
    }
  };

  // Get YouTube embed URL from regular URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats
    let videoId = null;
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : url;
  };

  // Check if content's contracting date has passed (for directo/encore)
  const isContentPast = () => {
    if (!content) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // For live content, check live_date
    if (content.isLive && content.liveDate) {
      const liveDate = new Date(content.liveDate);
      liveDate.setHours(0, 0, 0, 0);
      return liveDate < today;
    }
    
    // For encore content, check encore_end_date
    if (content.isEncore && content.encoreEndDate) {
      const encoreEnd = new Date(content.encoreEndDate);
      encoreEnd.setHours(0, 0, 0, 0);
      return encoreEnd < today;
    }
    
    return false;
  };

  const contentIsPast = isContentPast();

  const handleContratar = () => {
    const cinemaSession = localStorage.getItem('cinema_session');
    if (cinemaSession) {
      // Already logged in as cinema, go to booking with this content
      navigate('/cinema/booking', { state: { contentId: content.id } });
    } else {
      // Not logged in, go to cinema login (which has register option)
      // Save the contentId in session storage for after login
      sessionStorage.setItem('pending_booking_content', content.id);
      navigate('/cinema/login');
    }
  };

  const chapterImages = [
    content.image,
    "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&q=80",
    "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&q=80",
    "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&q=80",
    "https://images.unsplash.com/photo-1460723237483-7a6dc9d0b212?w=400&q=80",
    "https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=400&q=80",
    "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=400&q=80"
  ];

  // Generate SEO content
  const seoDescription = content.synopsis 
    ? content.synopsis.substring(0, 160) + (content.synopsis.length > 160 ? '...' : '')
    : `${content.title} - Disfruta de ${content.category || 'contenido cultural'} en tu cine favorito con Bravo CinemaStreaming.`;
  
  const seoImage = content.heroImage || content.image;
  const seoUrl = `https://www.cinemastreaming.es/content/${content.id}`;
  const seoKeywords = [
    content.title,
    content.composer,
    content.category,
    ...(content.tags || [])
  ].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Dynamic SEO Meta Tags */}
      <SEOHead 
        title={content.title}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        url={seoUrl}
        type="video.movie"
        schema={generateEventSchema(content)}
      />
      
      <Header onOpenAuth={() => setAuthOpen(true)} onOpenSearch={() => setSearchOpen(true)} />

      {/* Hero */}
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${content.heroImage || content.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-12 max-w-[1920px] mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80 hover:text-white mb-8">
            <ArrowLeft className="w-5 h-5" /><span className="text-sm">Volver</span>
          </button>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {content.tags?.slice(0, 4).map((tag, i) => (
              <span key={i} className="px-3 py-1 bg-white/10 text-white/80 text-xs rounded-full">{tag}</span>
            ))}
          </div>
          
          <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold mb-2">{content.title}</h1>
          <p className="text-[#e4002b] text-xl md:text-2xl font-medium mb-1">{content.composer}</p>
          {(content.theater || content.productInfo?.theater_name) && (
            <p className="text-white/70 text-lg mb-6">{content.theater || content.productInfo?.theater_name}</p>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-8">
            <button 
              onClick={handleTrailer}
              className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white font-medium rounded hover:bg-white/20"
            >
              <Play className="w-4 h-4" />TRAILER
            </button>
            <button 
              onClick={toggleFavorite}
              className={`flex items-center gap-2 px-6 py-3 border rounded transition-colors ${
                isFavorite ? 'bg-[#e4002b] border-[#e4002b]' : 'border-white/20 hover:bg-white/10'
              } text-white`}
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              {isFavorite ? 'EN FAVORITOS' : 'AÑADIR'}
            </button>
            {/* Only show CONTRATAR button if content hasn't passed */}
            {!contentIsPast && (
              <button 
                onClick={handleContratar}
                className="flex items-center gap-2 px-6 py-3 bg-[#e4002b] text-white font-medium rounded hover:bg-[#c70024]"
              >
                <FileText className="w-4 h-4" />CONTRATAR
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 md:px-12 lg:px-24 py-12 max-w-[1920px] mx-auto">
        {/* Past content message */}
        {contentIsPast && (
          <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-lg flex items-center gap-3">
            <Lock className="w-5 h-5 text-white/50" />
            <p className="text-white/70 text-sm">
              Este contenido ya no está disponible para contratación, solo a modo de consulta.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-8 border-b border-white/10 mb-8">
          {tabs.map(tab => (
            <button 
              key={tab} 
              onClick={() => {
                if (tab === 'MATERIALES' && !isCinemaApproved) {
                  // Don't switch tab, show message
                  return;
                }
                setActiveTab(tab);
              }} 
              className={`pb-4 text-sm font-medium tracking-wider transition-colors relative flex items-center gap-2 ${
                activeTab === tab ? 'text-white' : 'text-white/50 hover:text-white/80'
              } ${tab === 'MATERIALES' && !isCinemaApproved ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {tab === 'MATERIALES' && !isCinemaApproved && <Lock className="w-4 h-4" />}
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#e4002b]" />}
            </button>
          ))}
        </div>

        {/* Materials locked message */}
        {activeTab !== 'MATERIALES' && !isCinemaApproved && (
          <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-lg flex items-center gap-3">
            <Lock className="w-5 h-5 text-white/50" />
            <p className="text-white/70 text-sm">
              La pestaña <strong>MATERIALES</strong> está disponible solo para cines registrados y aprobados.{' '}
              <button onClick={() => navigate('/cinema/login')} className="text-[#e4002b] hover:underline">
                Acceder como cine
              </button>
            </p>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'INFO' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Columna Izquierda - Sinopsis + Información de la producción */}
            <div className="lg:col-span-2 space-y-8">
              {/* Synopsis */}
              <div>
                <h3 className="text-white text-lg font-semibold mb-4">Sinopsis</h3>
                <p className="text-white/70 leading-relaxed whitespace-pre-wrap">{content.synopsis}</p>
                {content.synopsisExtended && <p className="text-white/70 leading-relaxed mt-4 whitespace-pre-wrap">{content.synopsisExtended}</p>}
              </div>
              
              {/* Información de la producción */}
              {(content.productInfo && (content.productInfo.theater_name || content.productInfo.recording_year || content.productInfo.duration || content.productInfo.subtitles_version)) || (content.isLive && (content.liveDate || content.liveTime)) ? (
                <div>
                  <h3 className="text-white text-lg font-semibold mb-4">Información de la producción</h3>
                  <div className="bg-[#141414] rounded-lg p-6">
                    {/* Emisión del Directo - Solo para contenidos en directo */}
                    {content.isLive && (content.liveDate || content.liveTime) && (
                      <div className="mb-6 pb-6 border-b border-white/10">
                        <div className="flex items-center gap-2 mb-4">
                          <Radio className="w-4 h-4 text-[#e4002b]" />
                          <span className="text-[#e4002b] text-sm font-bold uppercase tracking-wider">Emisión en Directo</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {content.liveDate && (
                            <div className="flex items-center gap-3">
                              <Calendar className="w-5 h-5 text-[#e4002b] flex-shrink-0" />
                              <div>
                                <p className="text-white/50 text-xs uppercase">Día de emisión</p>
                                <p className="text-white font-semibold">
                                  {new Date(content.liveDate + 'T00:00:00').toLocaleDateString('es-ES', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}
                                </p>
                              </div>
                            </div>
                          )}
                          {content.liveTime && (
                            <div className="flex items-center gap-3">
                              <Clock className="w-5 h-5 text-[#e4002b] flex-shrink-0" />
                              <div>
                                <p className="text-white/50 text-xs uppercase">Hora de emisión</p>
                                <p className="text-white font-semibold">{content.liveTime}h</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {content.productInfo?.theater_name && (
                        <div className="flex items-center gap-3">
                          <Film className="w-5 h-5 text-[#e4002b] flex-shrink-0" />
                          <div>
                            <p className="text-white/50 text-xs uppercase">Teatro</p>
                            <p className="text-white">{content.productInfo.theater_name}</p>
                          </div>
                        </div>
                      )}
                      {content.productInfo?.recording_year && (
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-[#e4002b] flex-shrink-0" />
                          <div>
                            <p className="text-white/50 text-xs uppercase">Año de grabación</p>
                            <p className="text-white">{content.productInfo.recording_year}</p>
                          </div>
                        </div>
                      )}
                      {content.productInfo?.duration && (
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-[#e4002b] flex-shrink-0" />
                          <div>
                            <p className="text-white/50 text-xs uppercase">Duración</p>
                            <p className="text-white">{content.productInfo.duration}</p>
                          </div>
                        </div>
                      )}
                      {content.productInfo?.subtitles_version && (
                        <div className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-[#e4002b] flex-shrink-0" />
                          <div>
                            <p className="text-white/50 text-xs uppercase">Subtítulos</p>
                            <p className="text-white">{content.productInfo.subtitles_version}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            
            {/* Columna Derecha - Equipo Artístico + Reparto */}
            <div className="space-y-8">
              {/* Artistic Credits */}
              {content.artisticCredits?.length > 0 && (
                <div>
                  <h3 className="text-white text-lg font-semibold mb-4">Equipo Artístico</h3>
                  <div className="bg-[#141414] rounded-lg p-6">
                    {content.artisticCredits.map((credit, i) => <CreditItem key={i} credit={credit} />)}
                  </div>
                </div>
              )}
              
              {/* Cast */}
              {content.cast?.length > 0 && (
                <div>
                  <h3 className="text-white text-lg font-semibold mb-4">Reparto</h3>
                  <div className="bg-[#141414] rounded-lg p-6">
                    {content.cast.map((member, i) => <CastItem key={i} member={member} />)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'MULTIMEDIA' && (
          <div>
            {/* Display uploaded media (read-only) */}
            {mediaItems.length > 0 && (
              <div className="mb-8">
                <h3 className="text-white text-lg font-semibold mb-4">Galería</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaItems.filter(m => m.type === 'image').map(media => (
                    <div key={media.id} className="aspect-video rounded-lg overflow-hidden bg-[#1f1f1f]">
                      <img src={media.url} alt={media.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mediaItems.filter(m => m.type === 'video').length > 0 && (
              <div className="mb-8">
                <h3 className="text-white text-lg font-semibold mb-4">Videos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mediaItems.filter(m => m.type === 'video').map(media => (
                    <div key={media.id} className="aspect-video rounded-lg overflow-hidden bg-[#1f1f1f]">
                      <video src={media.url} controls className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Chapters */}
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Contenido disponible</h3>
              <div className="grid gap-3">
                {content.chapters?.map((chapter, i) => (
                  <ChapterItem key={chapter.id} chapter={chapter} image={chapterImages[i % chapterImages.length]} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MATERIALES Tab */}
        {activeTab === 'MATERIALES' && isCinemaApproved && (
          <div>
            <div className="mb-6 p-4 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded-lg">
              <p className="text-white/80 text-sm">
                <strong>Bienvenido, {cinemaSession?.venue_name || 'Cine'}</strong> - Aquí encontrarás todos los materiales promocionales disponibles para este contenido.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Cartel - Más pequeño, proporcional */}
              <div className="lg:col-span-1">
                <h3 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
                  <FileImage className="w-4 h-4 text-[#e4002b]" />
                  Cartel
                </h3>
                <div className="bg-[#141414] rounded-xl overflow-hidden">
                  {/* Poster Thumbnail - Más pequeño */}
                  <div className="aspect-[2/3] bg-[#1f1f1f] relative group max-h-[280px]">
                    <img 
                      src={content.materials?.poster_thumbnail || content.image || content.heroImage} 
                      alt={`Cartel de ${content.title}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs">Vista previa</span>
                    </div>
                  </div>
                  
                  {/* Download buttons - Más compactos */}
                  <div className="p-3 space-y-2">
                    <a 
                      href={content.materials?.poster_web_url || content.image || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="flex items-center justify-between w-full px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                    >
                      <span className="text-white/80 text-xs flex items-center gap-2">
                        <Image className="w-3 h-3" />
                        Web
                      </span>
                      <Download className="w-3 h-3 text-[#e4002b] group-hover:scale-110 transition-transform" />
                    </a>
                    <a 
                      href={content.materials?.poster_print_url || content.image || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="flex items-center justify-between w-full px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                    >
                      <span className="text-white/80 text-xs flex items-center gap-2">
                        <Printer className="w-3 h-3" />
                        Imprenta
                      </span>
                      <Download className="w-3 h-3 text-[#e4002b] group-hover:scale-110 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Resto de materiales - Ahora ocupa 3 columnas */}
              <div className="lg:col-span-3">
                <h3 className="text-white text-base font-semibold mb-3 flex items-center gap-2">
                  <Download className="w-4 h-4 text-[#e4002b]" />
                  Materiales de promoción
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Trailer */}
                  <MaterialCard 
                    icon={<Play className="w-5 h-5" />}
                    title="Trailer"
                    description="Video promocional en alta calidad"
                    url={content.materials?.trailer_download_url || content.trailerUrl || content.trailer_url}
                    isExternal={true}
                  />

                  {/* Imágenes */}
                  <MaterialCard 
                    icon={<Image className="w-5 h-5" />}
                    title="Imágenes"
                    description="Pack de imágenes promocionales"
                    url={content.materials?.images_url}
                  />

                  {/* Sinopsis */}
                  <MaterialCard 
                    icon={<FileText className="w-5 h-5" />}
                    title="Sinopsis"
                    description="Texto descriptivo para difusión"
                    url={content.materials?.synopsis_url}
                  />

                  {/* Hoja de mano */}
                  <MaterialCard 
                    icon={<FileText className="w-5 h-5" />}
                    title="Hoja de mano"
                    description="Material para entregar al público"
                    url={content.materials?.handout_url}
                  />

                  {/* Nota de prensa */}
                  <MaterialCard 
                    icon={<Newspaper className="w-5 h-5" />}
                    title="Nota de prensa"
                    description="Comunicado oficial para medios"
                    url={content.materials?.press_release_url}
                  />

                  {/* Assets RRSS */}
                  <MaterialCard 
                    icon={<Share2 className="w-5 h-5" />}
                    title="Assets RRSS"
                    description="Contenido para redes sociales"
                    url={content.materials?.social_media_assets_url}
                  />
                </div>

                {/* No materials message */}
                {!content.materials && (
                  <div className="mt-6 p-6 bg-white/5 border border-white/10 rounded-lg text-center">
                    <FileText className="w-10 h-10 text-white/30 mx-auto mb-3" />
                    <p className="text-white/50">
                      Los materiales de este contenido están siendo preparados.<br />
                      Vuelve pronto para descargarlos.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <Footer />
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      
      {/* Trailer Modal */}
      {showTrailerModal && (content.trailerUrl || content.trailer_url) && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowTrailerModal(false)}
        >
          <div className="relative w-full max-w-5xl aspect-video" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowTrailerModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#e4002b] text-xl font-bold"
            >
              ✕ Cerrar
            </button>
            <iframe
              src={getYouTubeEmbedUrl(content.trailerUrl || content.trailer_url)}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Trailer"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentDetailPage;
